import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowUpRight, Package, Globe as GlobeIcon, Headset, ShieldCheck } from "@phosphor-icons/react";
import Globe from "@/components/Globe";
import { MaskedLines, Reveal, Stagger, StaggerItem } from "@/components/Motion";
import { COMPANY, TRUST, SERVICES } from "@/data/content";

const ICONS = { sourcing: Package, trading: GlobeIcon, operations: Headset };

const Hero = () => (
  <section data-testid="hero" className="relative min-h-[100svh] bg-[#050a11] text-white overflow-hidden flex items-center">
    <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[80vw] max-w-[720px] aspect-square opacity-90 pointer-events-none">
      <Globe />
    </div>
    <div className="absolute inset-0 bg-gradient-to-r from-[#050a11] via-[#050a11]/70 to-transparent md:to-transparent" />

    <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 pt-28">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.8 }}
        className="overline text-[#9dc0ff] mb-8 flex items-center gap-3"
      >
        <span className="w-8 h-px bg-[#9dc0ff]/60" /> {COMPANY.legal} — {COMPANY.ssm}
      </motion.p>

      <h1 className="font-display font-light text-5xl md:text-7xl lg:text-[6.2rem] leading-[0.92] tracking-tight max-w-4xl">
        <MaskedLines lines={COMPANY.tagline} delay={0.35} />
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.9 }}
        className="mt-10 max-w-xl text-white/65 text-base md:text-lg leading-relaxed"
      >
        {COMPANY.intro}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15, duration: 0.9 }}
        className="mt-12 flex flex-wrap items-center gap-4"
      >
        <Link
          to="/contact"
          data-testid="hero-cta-primary"
          className="group inline-flex items-center gap-2 bg-white text-[#0b2046] font-medium rounded-full pl-7 pr-5 py-4 hover:bg-[#9dc0ff] active:scale-[0.98] transition-[background-color,transform]"
        >
          Start a partnership
          <ArrowUpRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <Link
          to="/services"
          data-testid="hero-cta-secondary"
          className="inline-flex items-center gap-2 border border-white/25 rounded-full px-7 py-4 hover:bg-white/10 transition-colors"
        >
          Explore services
        </Link>
      </motion.div>
    </div>
  </section>
);

const TrustMarquee = () => (
  <section data-testid="trust-marquee" className="bg-[#f8f9fa] border-y border-black/10 py-10 md:py-14">
    <Marquee speed={28} gradient={false} autoFill>
      {TRUST.map((t, i) => (
        <span key={i} className="mx-8 md:mx-12 inline-flex items-center gap-8 md:gap-12">
          <span className="font-display text-2xl md:text-4xl text-[#0b2046] tracking-tight">{t}</span>
          <span className="text-[#0b2046]/30 text-2xl md:text-4xl">✦</span>
        </span>
      ))}
    </Marquee>
  </section>
);

const ServicesOverview = () => (
  <section data-testid="services-overview" className="bg-[#f8f9fa] py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10">
      <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
        <Reveal className="md:col-span-7">
          <p className="overline text-[#0b2046]/50 mb-5">What we do</p>
          <h2 className="font-display font-light text-4xl md:text-6xl leading-none tracking-tight text-[#050a11]">
            One partner across the entire supply chain.
          </h2>
        </Reveal>
        <Reveal className="md:col-span-5 md:text-right" delay={0.15}>
          <p className="text-[#4b5563] max-w-md md:ml-auto">
            From factory floor to your customer's doorstep — we source, supply and operate so your brand can scale.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.id];
          return (
            <Reveal
              key={s.id}
              delay={i * 0.1}
              className="group bg-[#f8f9fa] hover:bg-[#0b2046] p-8 md:p-10 flex flex-col min-h-[360px] transition-colors duration-500"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono-x text-sm text-[#0b2046]/40 group-hover:text-white/40 transition-colors">{s.no}</span>
                <Icon size={30} weight="duotone" className="text-[#0b2046] group-hover:text-[#9dc0ff] transition-colors" />
              </div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-16 text-[#050a11] group-hover:text-white transition-colors">
                {s.title}
              </h3>
              <p className="mt-4 text-[#4b5563] group-hover:text-white/70 transition-colors flex-1">{s.short}</p>
              <Link
                to="/services"
                data-testid={`service-card-link-${s.id}`}
                className="mt-6 inline-flex items-center gap-2 font-medium text-[#0b2046] group-hover:text-white transition-colors"
              >
                Learn more
                <ArrowUpRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Regions = () => (
  <section data-testid="regions" className="bg-[#050a11] text-white py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <Reveal>
          <p className="overline text-[#9dc0ff] mb-6">Global reach</p>
          <h2 className="font-display font-light text-4xl md:text-6xl leading-none tracking-tight">
            Sourced in China. Supplied worldwide.
          </h2>
          <p className="mt-8 text-white/60 max-w-md leading-relaxed">
            We supply vetted consumer-safety products to international brand partners across five key markets.
          </p>
          <div className="mt-10 flex items-center gap-3 text-white/70">
            <ShieldCheck size={22} weight="duotone" className="text-[#9dc0ff]" />
            <span className="text-sm">Quality-controlled at source, compliant at destination.</span>
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-7">
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {COMPANY.regions.map((r, i) => (
            <StaggerItem key={r} className="bg-[#050a11] p-7 md:p-8 flex items-baseline justify-between hover:bg-[#0a121f] transition-colors">
              <span className="font-display text-xl md:text-2xl tracking-tight">{r}</span>
              <span className="font-mono-x text-xs text-white/30">{String(i + 1).padStart(2, "0")}</span>
            </StaggerItem>
          ))}
          <StaggerItem className="bg-[#0b2046] p-7 md:p-8 flex items-center">
            <span className="font-mono-x text-xs text-[#9dc0ff] tracking-widest">+ EXPANDING</span>
          </StaggerItem>
        </Stagger>
      </div>
    </div>
  </section>
);

const Home = () => (
  <div data-testid="page-home">
    <Hero />
    <TrustMarquee />
    <ServicesOverview />
    <Regions />
  </div>
);

export default Home;
